import * as React from "react";
import { Tree, TreeNode } from 'react-organizational-chart';
import styled from 'styled-components';
import { OrganizationOwner, userHelper, WorkgroupListing, WorkgroupMember } from '../../../amorg-common';
import { UserAvatar } from "../../../designSystem/avatar/UserAvatar";
import { Button } from "../../../designSystem/button/Button";
import { colors } from "../../../designSystem/colors/color";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { Icon } from "../../../designSystem/icons/Icons";
import { P, Small } from "../../../designSystem/typography/Body";
import { H3 } from "../../../designSystem/typography/Heading";
import { getRootNodes, WorkgroupListingNode } from './OrgChartHelper';

// https://www.npmjs.com/package/react-organizational-chart

type OrgChartProps = {
    organizationOwner: OrganizationOwner | null;
    allWorkgroups: WorkgroupListing[],
    onClickCreateNew?: () => void,
    onClickEditGroup?: (workgroupId: string) => void
    onClickEditRootGroup?: () => void,
}
export function OrgChart(props: OrgChartProps): JSX.Element {

    const rootNodes = getRootNodes(props.allWorkgroups);

    return (
        <>
            {props.allWorkgroups &&
                <Tree 
                    label={
                        <RootNode organizationOwner={props.organizationOwner} onClickEditRootGroup={props.onClickEditRootGroup} />
                    }>
                    {rootNodes.map((item) => (
                        <ChartNode
                            key={item.workgroup.id}
                            node={item}
                            onClickEditWorkgroup={props.onClickEditGroup} />
                    ))}
                </Tree>
            }
        </>
    );
}

type RootNodeProps = {
    onClickEditRootGroup?: () => void;
    organizationOwner: OrganizationOwner | null;
}
function RootNode(props: RootNodeProps): JSX.Element {
    return (
        <WorkgroupCard
            title="Organisationsansvarlig"
            subTitle={props.organizationOwner 
                ? userHelper.getPublicUserFullName(props.organizationOwner)
                : "Ingen ansvarlig registreret"
            }
            bgColor={colors.org.card.background.root}
            onClick={props.onClickEditRootGroup}>
        </WorkgroupCard>
    )
}

type ChartNodeProps = {
    node: WorkgroupListingNode,
    onClickEditWorkgroup?: (workgroupId: string) => void
}
function ChartNode(props: ChartNodeProps): JSX.Element {
    return (
        <TreeNode label={
            <WorkgroupRepresentation wg={props.node} onClickEditWorkgroup={props.onClickEditWorkgroup} /> }>
            
            {props.node.children.map((child) => (
                <ChartNode
                    key={child.workgroup.id}
                    node={child}
                    onClickEditWorkgroup={props.onClickEditWorkgroup} />
            ))}
        </TreeNode>
    );
}
type WorkgroupRepresentationProps = {
    wg: WorkgroupListingNode
    onClickEditWorkgroup?: (workgroupId: string) => void
}
function WorkgroupRepresentation(props: WorkgroupRepresentationProps): JSX.Element {

    const group = props.wg.workgroup;

    const bgcolor = props.wg.workgroup.type === 'committee'
        ? colors.org.card.background.committee
        : colors.org.card.background.workgroup;

    return (
        <WorkgroupCard
            key={group.id}
            title={group.name}
            subTitle={group.type === 'committee' ? 'Udvalg' : 'Arbejdsgruppe'}
            onClick={
                props.onClickEditWorkgroup
                    ? () => props.onClickEditWorkgroup?.(group.id)
                    : undefined
            }
            bgColor={bgcolor}>
            <div>
                {group.members.length === 0 &&
                    <P>
                        Ingen medlemmer tilføjet
                    </P>
                }

                {group.members.length > 0 && 
                    <Flex flexDirection="column" gap={4} style={{marginTop:'10px'}}>
                        {group.members.map((member) => {
                            return (
                                <MemberRow key={member.userId} member={member} />
                            )
                        })}
                    </Flex>
                }
            </div>
        </WorkgroupCard>
    )
}


type WorkgroupCardProps = {
    title:string,
    subTitle:string,
    bgColor:string,
    children?:React.ReactNode,
    onClick?:() => void
}
function WorkgroupCard(props:WorkgroupCardProps):JSX.Element
{
    return (
        <WorkgroupCardWrapper
            bgcolor={props.bgColor}>

            {props.onClick &&
                <CardHeader>
                    <Button variant="text" icon='navigation_settings' text="" onClick={() => props.onClick?.()} size={"42px"}/>
                </CardHeader>
            }

            <CardContent>
                <CardTitle title={props.title}>{props.title}</CardTitle>
                <Small>{props.subTitle}</Small>
                
                {props.children}
            </CardContent>
            
        </WorkgroupCardWrapper>
    );
}


const WorkgroupCardWrapper = styled.div<{ bgcolor: string }>`

    display: grid;
    grid-template-columns:1fr;
    grid-template-rows: auto 1fr;

    grid-template-areas:
        "top"
        "content";
    
    border-radius:${props => props.theme.styles.card.borderRadius};
    border:${props => `1px solid ${props.theme.colors.card.outlined.active.border}`};
    background-color: ${props => props.bgcolor};
    
    justify-self: center;
    
    min-width: 200px;
    max-width: 300px;
    font-size: 0.9rem;
    background-color:${props => props.bgcolor};
    position: relative;
    overflow:hidden;
`;

const CardHeader = styled.div`
    grid-area: top;
    justify-content: flex-end;
    display: flex;
    width: 100%;
    border-bottom:${props => `1px solid ${props.theme.colors.card.outlined.active.border}`};
`;

const CardContent = styled.div`
    grid-area: content;
    min-width: 0;
    padding: 8px 16px;
`;

const CardTitle = styled(H3)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

type MemberRowProps = {
    member:WorkgroupMember
}
function MemberRow(props:MemberRowProps):JSX.Element
{
    return (
        <Flex gap={8} style={{minHeight:60}}>
            <UserAvatar
                key={props.member.userId}
                user={{
                    displayName: props.member.firstname + ' ' + props.member.lastname,
                    initials: props.member.initials
                }}
                size="32px"
                backgroundColorOverride={colors.getUserRoleColor(props.member.roleId)}
                hideTooltip
            />
            <Flex flexDirection="column" style={{justifyContent:'start', alignItems:'flex-start', minWidth:0, flex:1}}>
                <span style={{textAlign:'left', display:'block', width:'100%', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                    {props.member.firstname} {props.member.lastname}
                </span>
                {props.member.roleId &&
                    <span style={{textAlign:'left', fontSize:12, display:'block', width:'100%', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                        {props.member.roleTitle}
                    </span>
                }
                <Flex alignItems="center" gap={4} style={{fontSize:12}}>
                    <a href={`mailto:${props.member.email}`} style={{display:'flex', alignItems:'center', gap:4, color: colors.text.gray.normal}}>
                        <Icon name="interface_glyph_email" size="x-small" color={colors.text.gray.normal} />
                    </a>
                    <span style={{color: colors.text.gray.normal}}>{props.member.email}</span>
                </Flex>
            </Flex>
        </Flex>
    );
}